import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Play, 
  Pause, 
  Clock, 
  Calendar, 
  AlertCircle, 
  CheckCircle2, 
  XCircle,
  Plus,
  Edit,
  Trash2,
  History,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/lib/supabase';
import { useAuthContext } from '@/contexts/AuthContext';
import { 
  SystemJob, 
  SystemJobStatus, 
  SystemJobScheduleFrequency, 
  SystemJobExecution, 
  SystemJobExecutionStatus 
} from '@/types';
import { StatusBadge } from '@/components/common/StatusBadge';

// Interface para o formulário de job
interface JobFormData {
  name: string;
  description?: string;
  function_name: string;
  function_params?: Record<string, any>;
  status: SystemJobStatus;
  schedule_frequency: SystemJobScheduleFrequency;
  schedule_time?: string;
  schedule_day?: number;
}

interface EdgeFunction {
  id: string;
  name: string;
  slug: string;
  status: string;
  version: number;
  updated_at: number;
  created_at: number;
}

// Função auxiliar para formatar data com segurança
const formatDate = (dateString: string | number | null | undefined) => {
  if (!dateString) return '-';
  try {
    const date = typeof dateString === 'number' 
      ? new Date(dateString) // Unix timestamp em milissegundos
      : new Date(dateString);
    
    if (isNaN(date.getTime())) return '-';
    return format(date, "dd/MM/yyyy HH:mm:ss", { locale: ptBR });
  } catch (error) {
    return '-';
  }
};

// Componente para o histórico de execuções
function ExecutionHistory({ jobId }: { jobId: string }) {
  const [executions, setExecutions] = useState<SystemJobExecution[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadExecutions();
  }, [jobId]);

  const loadExecutions = async () => {
    try {
      const { data, error } = await supabase
        .from('system_job_executions')
        .select('*')
        .eq('job_id', jobId)
        .order('started_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      setExecutions(data as SystemJobExecution[] || []);
    } catch (error) {
      toast.error('Erro ao carregar histórico de execuções');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-extrabold">Histórico de Execuções</h3>
        <Button variant="outline" size="sm" onClick={loadExecutions}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Atualizar
        </Button>
      </div>
      
      {loading ? (
        <div className="text-center py-4">Carregando...</div>
      ) : executions.length === 0 ? (
        <div className="text-center py-4 text-muted-foreground">Nenhuma execução encontrada</div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Data/Hora</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Tempo</TableHead>
              <TableHead>Resultado</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {executions.map((execution) => (
              <TableRow key={execution.id}>
                <TableCell>
                  {formatDate(execution.started_at)}
                </TableCell>
                <TableCell>
                  <StatusBadge status={execution.status} context="job-execution" />
                </TableCell>
                <TableCell>
                  {execution.execution_time_ms ? `${(execution.execution_time_ms / 1000).toFixed(2)}s` : '-'}
                </TableCell>
                <TableCell className="max-w-md truncate">
                  {execution.error_message ? (
                    <span className="text-destructive">{execution.error_message}</span>
                  ) : execution.result ? (
                    <pre className="text-xs">{JSON.stringify(execution.result, null, 2)}</pre>
                  ) : '-'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

export default function SystemJobs() {
  const { user } = useAuthContext();
  const [jobs, setJobs] = useState<SystemJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [edgeFunctions, setEdgeFunctions] = useState<EdgeFunction[]>([]);
  const [showJobForm, setShowJobForm] = useState(false);
  const [showExecutionHistory, setShowExecutionHistory] = useState(false);
  const [selectedJob, setSelectedJob] = useState<SystemJob | null>(null);
  const [formData, setFormData] = useState<JobFormData>({
    name: '',
    description: '',
    function_name: '',
    function_params: {},
    status: 'draft',
    schedule_frequency: 'daily',
    schedule_time: '00:00',
    schedule_day: 1
  });
  const [isLoadingFunctions, setIsLoadingFunctions] = useState(false);

  useEffect(() => {
    loadJobs();
    loadEdgeFunctions();
  }, []);

  const loadJobs = async () => {
    try {
      const { data, error } = await supabase
        .from('system_jobs')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setJobs(data as SystemJob[] || []);
    } catch (error) {
      toast.error('Erro ao carregar jobs');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadEdgeFunctions = async () => {
    try {
      setIsLoadingFunctions(true);
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/list-edge-functions`, {
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_MANAGEMENT_TOKEN}`
        }
      });
      const data = await response.json();
      setEdgeFunctions(data);
    } catch (error) {
      console.error('Error loading edge functions:', error);
      toast.error('Erro ao carregar funções disponíveis. Por favor, tente novamente.');
    } finally {
      setIsLoadingFunctions(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.function_name) {
      toast.error('Nome do job e nome da função são obrigatórios.');
      return;
    }
    
    const organizationId = '00000000-0000-0000-0000-000000000000';
    if (!import.meta.env.PROD) console.warn('TODO: Using placeholder organization_id in SystemJobs');

    const jobData = {
      ...formData,
      organization_id: organizationId,
      function_params: typeof formData.function_params === 'string' 
        ? JSON.parse(formData.function_params || '{}') 
        : formData.function_params || {},
      schedule_day: formData.schedule_frequency === 'monthly' ? formData.schedule_day : null,
    };

    try {
      if (selectedJob) {
        const { error } = await supabase
          .from('system_jobs')
          .update(jobData)
          .eq('id', selectedJob.id);

        if (error) throw error;
        toast.success('Job atualizado com sucesso');
      } else {
        const { data, error } = await supabase
          .from('system_jobs')
          .insert(jobData)
          .select()
          .single();
        
        if (error) throw error;
        setJobs([data as SystemJob, ...jobs]);
        toast.success('Job criado com sucesso');
      }
      setShowJobForm(false);
      setSelectedJob(null);
    } catch (error: any) {
      console.error('Erro ao salvar job:', error);
      toast.error(`Erro ao salvar job: ${error.message}`);
    }
  };

  const handleEdit = (job: SystemJob) => {
    setSelectedJob(job);
    setFormData({
      name: job.name,
      description: job.description || '',
      function_name: job.function_name,
      function_params: job.function_params || {},
      status: job.status,
      schedule_frequency: job.schedule_frequency,
      schedule_time: job.schedule_time || '00:00',
      schedule_day: job.schedule_day || 1
    });
    setShowJobForm(true);
  };

  const handleDelete = async (job: SystemJob) => {
    if (!confirm('Tem certeza que deseja excluir este job?')) return;

    try {
      const { error } = await supabase
        .from('system_jobs')
        .delete()
        .eq('id', job.id);

      if (error) throw error;
      toast.success('Job excluído com sucesso');
      loadJobs();
    } catch (error) {
      toast.error('Erro ao excluir job');
      console.error(error);
    }
  };

  const handleExecute = async (job: SystemJob) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/execute-system-job`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_MANAGEMENT_TOKEN}`
        },
        body: JSON.stringify({ job_id: job.id })
      });

      if (!response.ok) throw new Error('Erro ao executar job');

      const result = await response.json();
      
      if (result.success) {
        toast.success('Job executado com sucesso');
        loadJobs();
      } else {
        throw new Error(result.error || 'Erro ao executar job');
      }
    } catch (error) {
      toast.error('Erro ao executar job');
      console.error(error);
    }
  };

  const handleToggleStatus = async (job: SystemJob, newStatus: SystemJobStatus) => {
    try {
      const { error } = await supabase
        .from('system_jobs')
        .update({ status: newStatus })
        .eq('id', job.id);

      if (error) throw error;
      
      // Update jobs in state
      setJobs(prevJobs => 
        prevJobs.map(j => 
          j.id === job.id ? { ...j, status: newStatus } : j
        )
      );
      
      toast.success(`Job ${newStatus === 'active' ? 'ativado' : 'desativado'} com sucesso`);
    } catch (error) {
      console.error('Erro ao alterar status do job:', error);
      toast.error('Erro ao alterar status do job');
    }
  };

  const getScheduleText = (job: SystemJob) => {
    switch (job.schedule_frequency) {
      case 'once':
        return `Uma vez às ${job.schedule_time}`;
      case 'hourly':
        return 'A cada hora';
      case 'daily':
        return `Diariamente às ${job.schedule_time}`;
      case 'weekly':
        return `Semanalmente ${job.schedule_day ? `(dia ${job.schedule_day})` : ''} às ${job.schedule_time}`;
      case 'monthly':
        return `Mensalmente ${job.schedule_day ? `(dia ${job.schedule_day})` : ''} às ${job.schedule_time}`;
      default:
        return job.schedule_frequency;
    }
  };

  const handleDeleteFunction = async (fnSlug: string) => {
    if (!confirm(`Tem certeza que deseja excluir a função ${fnSlug}?`)) return;

    try {
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-edge-functions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_MANAGEMENT_TOKEN}`
        },
        body: JSON.stringify({ function_slug: fnSlug })
      });

      if (!response.ok) {
        throw new Error(`Erro ao excluir função: ${response.statusText}`);
      }
      toast.success('Função excluída com sucesso');
      loadEdgeFunctions();
    } catch (error: any) {
      toast.error(error.message || 'Erro ao excluir função');
      console.error(error);
    }
  };

  return (
    <div className="container mx-auto py-6">
      <Helmet>
        <title>CSM | Trivium - System Jobs</title>
      </Helmet>
      
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-extrabold">System Jobs</h1>
          <p className="text-muted-foreground">
            Gerencie tarefas automatizadas do sistema
          </p>
        </div>
        <Button onClick={() => {
          setSelectedJob(null);
          setFormData({
            name: '',
            description: '',
            function_name: '',
            function_params: {},
            status: 'draft',
            schedule_frequency: 'daily',
            schedule_time: '00:00',
            schedule_day: 1
          });
          setShowJobForm(true);
        }}>
          <Plus className="mr-2 h-4 w-4" />
          Novo Job
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Jobs do Sistema</CardTitle>
          <CardDescription>
            Visualize e gerencie jobs automatizados para processamento em segundo plano
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
            </div>
          ) : jobs.length === 0 ? (
            <div className="text-center py-10">
              <p className="text-muted-foreground">Nenhum job encontrado</p>
              <Button 
                className="mt-4" 
                variant="outline" 
                onClick={() => {
                  setSelectedJob(null);
                  setFormData({
                    name: '',
                    description: '',
                    function_name: '',
                    function_params: {},
                    status: 'draft',
                    schedule_frequency: 'daily',
                    schedule_time: '00:00',
                    schedule_day: 1
                  });
                  setShowJobForm(true);
                }}
              >
                <Plus className="mr-2 h-4 w-4" />
                Criar Primeiro Job
              </Button>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {jobs.map(job => (
                <Card key={job.id} className="overflow-hidden hover:shadow-md transition-shadow">
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                      <CardTitle className="text-lg">{job.name}</CardTitle>
                      <StatusBadge status={job.status} context="job" />
                    </div>
                    <CardDescription className="line-clamp-2">
                      {job.description || "Sem descrição"}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pb-2">
                    <div className="text-sm space-y-2">
                      <div className="flex items-center text-muted-foreground">
                        <Calendar className="h-4 w-4 mr-2" />
                        {getScheduleText(job)}
                      </div>
                      <div className="text-xs bg-secondary/50 rounded p-2 font-mono">
                        {job.function_name}
                      </div>
                    </div>
                  </CardContent>
                  <CardFooter className="flex justify-between pt-2 border-t bg-secondary/20">
                    <div className="flex space-x-1">
                      <Button variant="ghost" size="icon" onClick={() => handleEdit(job)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" 
                        onClick={() => {
                          setSelectedJob(job);
                          setShowExecutionHistory(true);
                        }}>
                        <History className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="flex space-x-1">
                      {job.status === 'active' ? (
                        <Button variant="ghost" size="icon" onClick={() => handleToggleStatus(job, 'inactive')} title="Pausar">
                          <Pause className="h-4 w-4" />
                        </Button>
                      ) : job.status === 'inactive' || job.status === 'draft' ? (
                        <Button variant="ghost" size="icon" onClick={() => handleToggleStatus(job, 'active')} title="Ativar">
                          <Play className="h-4 w-4" />
                        </Button>
                      ) : null}
                      <Button variant="ghost" size="icon" onClick={() => handleExecute(job)} title="Executar Agora">
                        <Play className="h-4 w-4 text-primary" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(job)} title="Excluir">
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </CardFooter>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Form Dialog */}
      <Dialog open={showJobForm} onOpenChange={setShowJobForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedJob ? 'Editar Job' : 'Novo Job'}</DialogTitle>
            <DialogDescription>
              Configure os detalhes e agendamento do job do sistema
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nome</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Nome do job"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description">Descrição</Label>
                <Textarea
                  id="description"
                  value={formData.description || ''}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Descrição do propósito deste job"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="function">Função a Executar</Label>
                <Select
                  value={formData.function_name}
                  onValueChange={(value) => setFormData({...formData, function_name: value})}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione uma função" />
                  </SelectTrigger>
                  <SelectContent>
                    {isLoadingFunctions ? (
                      <div className="p-2 text-center">
                        <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full inline-block"></div>
                        <span className="ml-2">Carregando...</span>
                      </div>
                    ) : edgeFunctions.length === 0 ? (
                      <div className="p-2 text-center text-muted-foreground">
                        Nenhuma função encontrada
                      </div>
                    ) : (
                      edgeFunctions.map(fn => (
                        <SelectItem key={fn.slug} value={fn.slug}>
                          {fn.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="params">Parâmetros (JSON)</Label>
                <Textarea
                  id="params"
                  value={formData.function_params ? JSON.stringify(formData.function_params, null, 2) : '{}'}
                  onChange={(e) => {
                    try {
                      const params = JSON.parse(e.target.value);
                      setFormData({...formData, function_params: params});
                    } catch (error) {
                      // Não atualizar se o JSON for inválido
                    }
                  }}
                  placeholder="{}"
                  className="font-mono"
                />
              </div>
              
              <Separator />
              
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({...formData, status: value as SystemJobStatus})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Rascunho</SelectItem>
                    <SelectItem value="active">Ativo</SelectItem>
                    <SelectItem value="inactive">Inativo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="frequency">Frequência</Label>
                <Select
                  value={formData.schedule_frequency}
                  onValueChange={(value) => setFormData({...formData, schedule_frequency: value as SystemJobScheduleFrequency})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione uma frequência" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hourly">A cada hora</SelectItem>
                    <SelectItem value="daily">Diariamente</SelectItem>
                    <SelectItem value="weekly">Semanalmente</SelectItem>
                    <SelectItem value="monthly">Mensalmente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {formData.schedule_frequency !== 'hourly' && (
                <div className="space-y-2">
                  <Label htmlFor="time">Horário</Label>
                  <Input
                    id="time"
                    type="time"
                    value={formData.schedule_time || '00:00'}
                    onChange={(e) => setFormData({...formData, schedule_time: e.target.value})}
                    required
                  />
                </div>
              )}
              
              {formData.schedule_frequency === 'weekly' && (
                <div className="space-y-2">
                  <Label htmlFor="day">Dia da Semana</Label>
                  <Select
                    value={String(formData.schedule_day || 1)}
                    onValueChange={(value) => setFormData({...formData, schedule_day: parseInt(value)})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o dia" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Segunda-feira</SelectItem>
                      <SelectItem value="2">Terça-feira</SelectItem>
                      <SelectItem value="3">Quarta-feira</SelectItem>
                      <SelectItem value="4">Quinta-feira</SelectItem>
                      <SelectItem value="5">Sexta-feira</SelectItem>
                      <SelectItem value="6">Sábado</SelectItem>
                      <SelectItem value="0">Domingo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {formData.schedule_frequency === 'monthly' && (
                <div className="space-y-2">
                  <Label htmlFor="day">Dia do Mês</Label>
                  <Select
                    value={String(formData.schedule_day || 1)}
                    onValueChange={(value) => setFormData({...formData, schedule_day: parseInt(value)})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o dia" />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 31 }, (_, i) => (
                        <SelectItem key={i + 1} value={(i + 1).toString()}>
                          {i + 1}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button variant="outline" type="button" onClick={() => setShowJobForm(false)}>
                Cancelar
              </Button>
              <Button type="submit">
                {selectedJob ? 'Atualizar' : 'Criar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Execution History Dialog */}
      <Dialog open={showExecutionHistory} onOpenChange={setShowExecutionHistory}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Histórico de Execuções</DialogTitle>
            <DialogDescription>
              {selectedJob && `Job: ${selectedJob.name}`}
            </DialogDescription>
          </DialogHeader>
          
          {selectedJob && (
            <ExecutionHistory jobId={selectedJob.id} />
          )}
          
          <DialogFooter>
            <Button onClick={() => setShowExecutionHistory(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
} 